import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { retry, timeout, timer, catchError, throwError } from 'rxjs';
import { LoggerService } from '../services/logger.service';

/**
 * HTTP Interceptor for retry logic and timeout handling.
 *
 * Features:
 * - 30 second timeout on all requests
 * - Auto-retry up to 3 times with exponential backoff
 * - Does NOT retry authentication errors (401, 403)
 * - Does NOT retry client errors (4xx except 408, 429)
 *
 * Exponential backoff delays:
 * - 1st retry: 1 second
 * - 2nd retry: 2 seconds
 * - 3rd retry: 4 seconds
 */
export const retryInterceptor: HttpInterceptorFn = (req, next) => {
  const logger = inject(LoggerService);

  return next(req).pipe(
    // Timeout after 30 seconds
    timeout(30000),

    // Retry with exponential backoff
    retry({
      count: 3,
      delay: (error, retryCount) => {
        const status = error.status;

        // Don't retry authentication/authorization errors
        if (status === 401 || status === 403) {
          logger.debug('Not retrying auth error', { status, url: req.url });
          throw error;
        }

        // Don't retry client errors (except 408 Request Timeout, 429 Too Many Requests)
        if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
          logger.debug('Not retrying client error', {
            status,
            url: req.url,
          });
          throw error;
        }

        // Exponential backoff: 1s, 2s, 4s
        const delayMs = Math.pow(2, retryCount - 1) * 1000;
        logger.info(`Retrying request (${retryCount}/3) after ${delayMs}ms`, {
          url: req.url,
          status,
        });

        return timer(delayMs);
      },
    }),

    // Log final errors
    catchError((error) => {
      logger.error('Request failed after retries', error, {
        url: req.url,
        method: req.method,
      });
      return throwError(() => error);
    }),
  );
};
